// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Modal as PingModal } from '../../components/Modal';
import { createTheme } from '../../design-system/theme';
import { leaderboardStrings } from './strings';
import type { LeaderboardColumnKind } from './types';

export interface NewLeaderboardColumnDraft {
  readonly title: string;
  readonly kind: LeaderboardColumnKind;
}

export interface NewLeaderboardColumnModalProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (draft: NewLeaderboardColumnDraft) => void;
}

const KIND_ORDER: readonly LeaderboardColumnKind[] = ['text', 'number', 'date', 'member', 'status'];

export const NewLeaderboardColumnModal: React.FC<NewLeaderboardColumnModalProps> = ({
  visible,
  onClose,
  onSubmit,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<LeaderboardColumnKind>('text');

  useEffect(() => {
    if (!visible) {
      setTitle('');
      setKind('text');
    }
  }, [visible]);

  const handleSubmit = useCallback(() => {
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      return;
    }
    onSubmit({ title: trimmed, kind });
    onClose();
  }, [title, kind, onSubmit, onClose]);

  return (
    <PingModal
      visible={visible}
      onClose={onClose}
      accessibilityLabel={leaderboardStrings.addColumnTitle}
    >
      <View>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {leaderboardStrings.addColumnTitle}
        </Text>
        <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
          {leaderboardStrings.columnNameLabel}
        </Text>
        <TextInput
          value={title}
          onChangeText={setTitle}
          placeholder={leaderboardStrings.columnNamePlaceholder}
          placeholderTextColor={theme.colors.text.tertiary}
          accessibilityLabel={leaderboardStrings.columnNameLabel}
          autoFocus
          style={[
            styles.input,
            {
              color: theme.colors.text.primary,
              borderColor: theme.colors.border.primary,
              backgroundColor: theme.colors.surface.secondary,
            },
          ]}
        />
        <Text style={[styles.label, { color: theme.colors.text.secondary }]}>
          {leaderboardStrings.columnKindLabel}
        </Text>
        <View style={styles.kindRow}>
          {KIND_ORDER.map((k) => {
            const selected = k === kind;
            return (
              <Pressable
                key={k}
                onPress={() => setKind(k)}
                accessibilityRole="button"
                accessibilityLabel={leaderboardStrings.columnKind(k)}
                accessibilityState={{ selected }}
                style={({ pressed }) => [
                  styles.kindChip,
                  {
                    borderColor: selected
                      ? theme.colors.accent.indigo
                      : theme.colors.border.secondary,
                    backgroundColor: selected ? `${theme.colors.accent.indigo}1A` : 'transparent',
                    opacity: pressed ? 0.7 : 1,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.kindChipText,
                    {
                      color: selected ? theme.colors.accent.indigo : theme.colors.text.primary,
                    },
                  ]}
                >
                  {leaderboardStrings.columnKind(k)}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <View style={styles.actions}>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={leaderboardStrings.cancelCta}
            style={({ pressed }) => [styles.actionButton, pressed && { opacity: 0.7 }]}
          >
            <Text style={[styles.cancelText, { color: theme.colors.text.secondary }]}>
              {leaderboardStrings.cancelCta}
            </Text>
          </Pressable>
          <Pressable
            onPress={handleSubmit}
            accessibilityRole="button"
            accessibilityLabel={leaderboardStrings.saveCta}
            accessibilityState={{ disabled: title.trim().length === 0 }}
            disabled={title.trim().length === 0}
            style={({ pressed }) => [
              styles.actionButton,
              styles.primaryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
              title.trim().length === 0 && { opacity: 0.4 },
            ]}
          >
            <Text style={styles.primaryText}>{leaderboardStrings.saveCta}</Text>
          </Pressable>
        </View>
      </View>
    </PingModal>
  );
};

const styles = StyleSheet.create({
  title: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 12,
  } satisfies TextStyle,
  label: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 6,
    marginTop: 12,
    textTransform: 'uppercase',
  } satisfies TextStyle,
  input: {
    fontSize: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  } satisfies TextStyle,
  kindRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  kindChip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    borderWidth: 1,
    marginRight: 6,
    marginBottom: 6,
  } satisfies ViewStyle,
  kindChipText: {
    fontSize: 12,
    fontWeight: '600',
  } satisfies TextStyle,
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 20,
  } satisfies ViewStyle,
  actionButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    marginLeft: 8,
  } satisfies ViewStyle,
  primaryButton: {
    minWidth: 80,
    alignItems: 'center',
  } satisfies ViewStyle,
  cancelText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  primaryText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
